import $ from 'jquery'
import { mapGetters, mapState } from 'vuex'
import { makeId } from '@utils/make-id'
import { intersectionViewportObserver } from '@utils/intersectionViewportObserver'
import VariantMixin from '@mixins/variant-mixin'
import '@accessible360/accessible-slick'
import '@accessible360/accessible-slick/slick/slick-theme.scss'
import '@accessible360/accessible-slick/slick/slick.scss'

export default {
  name: 'carousel',
  mixins: [
    VariantMixin(
      // Component name
      'carousel',
      // Automatic generated computed properties
      ['settings', 'waitForImages', 'transitionHeight']
    )
  ],
  props: {
    selector: {
      type: String
    },
    customSettings: {
      type: Object,
      default: () => ({})
    },
    barPagination: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    isMounted: false,
    canLoad: false,
    carousel: null,
    slick: null,
    images: null,
    slides: 0,
    imagesLoaded: 0,
    arrowPressed: false,
    activeDot: null
  }),
  computed: {
    ...mapState('responsive', ['breakpoints', 'width']),
    ...mapGetters('responsive', ['getValueForBestBreakpoint']),
    ...mapGetters('global', ['isRtl']),
    imagesLength() {
      return this.images ? this.images.length : 0
    },
    scope() {
      return {
        imageLoadedEvent: this.imageLoadedEvent,
        canLoad: this.canLoad
      }
    },
  },
  async mounted() {
    $('body')
      .css('overflowX', 'hidden')
    this.carousel = this.getCarouselElement()
    if (this.waitForImages) {
      const carouselTransitionHeight = this.getCarouselHeight()
      this.$el.style.height = carouselTransitionHeight + 'px'
      this.$el.style.overflowY = 'hidden'
      const overlay = document.createElement('DIV')
      overlay.classList.add('loading-overlay')
      this.$el.append(overlay)
    }
    if (!this.carousel.attr('id')) {
      this.carousel.attr('id', makeId(8))
    }
    if (!window.location.pathname.includes('/reservation/')) {
      await intersectionViewportObserver(this.$el, { checkIsVisible: false })
    }
    this.canLoad = true
    await this.mountCarousel()
    if (this.waitForImages) {
      this.unsetLoadingState()
    }
    this.bindSlideChangeListener()
    this.bindArrowListener()
    this.updateArrowsAccessibility()
  },
  beforeDestroy() {
    $('body')
      .css('overflowX', '')
    this.unbindSlideChangeListener()
    this.unbindArrowListener()
    // this.clearCarousel()
  },
  methods: {
    getCarouselHeight() {
      if (this.transitionHeight) {
        return this.getValueForBestBreakpoint(this.transitionHeight)
      } else {
        const slide = this.carousel.children('div:first-child')
        return slide.height()
      }
    },
    singleCarouselCheck(itemcount) {
      if (itemcount == 1) {
        $(this.$el)
          .find('.carousel-wrapper')
          .addClass('single-item container')
      }
    },
    mountCarousel() {
      return new Promise((resolve) => {
        if (this.waitForImages) {
          this.images = this.carousel.children('div')
          if (this.imagesLoaded >= this.imagesLength && !this.slick) {
            this.initCarousel(resolve)
          } else {
            this.$watch('imagesLoaded', (val) => {
              if (val >= this.imagesLength && !this.slick) {
                this.initCarousel(resolve)
              }
            })
          }
        } else {
          this.initCarousel(resolve)
        }
      })
    },
    getCarouselElement() {
      let carouselElement = $(this.$el)
      if (this.selector) {
        carouselElement = carouselElement.find(this.selector)
      }
      this.slides = carouselElement.children().length
      this.singleCarouselCheck(this.slides)
      return carouselElement
    },
    clearCarousel() {
      this.carousel.slick('unslick')
      this.slick = null
    },
    initCarousel(resolve) {
      return this.$nextTick(() => {
        if (!this.slick && this.slides > 1) {
          this.carousel.slick(this.transformSettingsBreakpoints())
          this.slick = this.carousel && this.carousel.slick('getSlick')
          this.isMounted = true
        }
        return resolve()
      })
    },
    transformSettingsBreakpoints() {
      let additionalSettings
      if (this.barPagination) {
        additionalSettings = {
          dots: true,
          dotsClass: 'bar-pagination',
        }
      }
      const transformed = {
        ...this.settings,
        ...additionalSettings,
        rtl: this.isRtl
      }
      if (Array.isArray(transformed.responsive)) {
        transformed.responsive = transformed.responsive.map((item) => {
          return {
            ...item,
            breakpoint: typeof item.breakpoint === 'string' ? this.breakpoints[item.breakpoint] : item.breakpoint
          }
        })
      }
      return transformed
    },
    imageLoadedEvent() {
      this.imagesLoaded++
    },
    unsetLoadingState() {
      const overlay = this.$el.querySelector('.loading-overlay')
      overlay.classList.add('off')
      setTimeout(() => {
        overlay.remove()
        this.$el.style.height = ''
        this.$el.style.overflowY = ''
      }, 1000)
    },
    bindArrowListener() {
      const carouselNavigation = this.$el.querySelector('.slick-dots')
      if (carouselNavigation) {
        carouselNavigation.addEventListener('keydown', this.arrowListenerCallback)
      }
    },
    unbindArrowListener() {
      const carouselNavigation = this.$el.querySelector('.slick-dots')
      if (carouselNavigation) {
        carouselNavigation.removeEventListener('keydown', this.arrowListenerCallback)
      }
    },
    arrowListenerCallback(event) {
      if (event.code === 'ArrowRight' || event.code === 'ArrowLeft') {
        event.stopPropagation()
        event.preventDefault()
        this.arrowPressed = true
      }
    },
    bindSlideChangeListener() {
      this.carousel.on('afterChange', this.slideChangeListenerCallback)
    },
    unbindSlideChangeListener() {
      this.carousel.off('afterChange', this.slideChangeListenerCallback)
    },
    slideChangeListenerCallback(event, slick, currentSlide, nextSlide) {
      if (this.arrowPressed) {
        this.activeDot = this.$el.querySelector('.slick-dots > li.slick-active > button')
        this.activeDot.focus()
        this.arrowPressed = false
      }
      this.updateArrowsAccessibility()
    },
    updateArrowsAccessibility() {
      const arrows = this.$el.querySelectorAll('.slick-arrow')
      arrows.forEach((arrow) => {
        arrow.disabled = false
        arrow.setAttribute('aria-disabled', arrow.classList.contains('slick-disabled') ? 'true' : 'false')
      })
    }
  },
  render() {
    return this.$scopedSlots.default(this.scope)
  }
}
